import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import {
  AdminSystemClubCreationErrors,
  AdminSystemHttpMetrics,
  AdminSystemJobs,
  AdminSystemWebhooks,
} from '../types';

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
};

const formatDuration = (value: number): string => `${value.toFixed(2)} ms`;
const formatSeconds = (value: number): string => `${value}s`;
const formatMemory = (value: number): string => `${value.toFixed(2)} MB`;

const SystemPage: React.FC = () => {
  const [httpMetrics, setHttpMetrics] = useState<AdminSystemHttpMetrics | null>(null);
  const [jobs, setJobs] = useState<AdminSystemJobs | null>(null);
  const [webhooks, setWebhooks] = useState<AdminSystemWebhooks | null>(null);
  const [clubCreationErrors, setClubCreationErrors] = useState<AdminSystemClubCreationErrors | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextHttpMetrics, nextJobs, nextWebhooks, nextClubCreationErrors] = await Promise.all([
        adminService.getSystemHttpMetrics(),
        adminService.listSystemJobs(),
        adminService.listSystemWebhooks(),
        adminService.listSystemClubCreationErrors(),
      ]);
      setHttpMetrics(nextHttpMetrics);
      setJobs(nextJobs);
      setWebhooks(nextWebhooks);
      setClubCreationErrors(nextClubCreationErrors);
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar a observabilidade tecnica');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const summary = httpMetrics?.summary;
  const webhookSummary = webhooks?.summary;

  const metricCards = useMemo(
    () =>
      summary
        ? [
            { eyebrow: 'HTTP', title: 'Requests 5m', value: String(summary.requests_last_5m), tone: 'emerald' as const },
            { eyebrow: 'Latencia', title: 'P95 HTTP', value: formatDuration(summary.p95_duration_ms), tone: 'blue' as const },
            { eyebrow: 'Erro', title: 'Taxa de erro', value: `${summary.error_rate_pct}%`, tone: 'rose' as const },
            { eyebrow: 'Loop', title: 'Event loop P95', value: formatDuration(summary.event_loop_p95_ms), tone: 'amber' as const },
            { eyebrow: 'Webhooks', title: 'Falhas 24h', value: String(webhookSummary?.failed_events_24h || 0), tone: 'rose' as const },
            { eyebrow: 'Incidents', title: 'Tecnicos abertos', value: String(summary.technical_incidents_open), tone: 'slate' as const },
          ]
        : [],
    [summary, webhookSummary?.failed_events_24h]
  );

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Sistema</p>
          <h1>Observabilidade tecnica da plataforma</h1>
          <p>Latencia HTTP, saude do processo, pool de banco, webhooks e o estado real da operacao tecnica.</p>
        </div>
        <button type="button" className="admin-secondary-button" onClick={() => void loadData()} disabled={loading}>
          {loading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}

      <section className="admin-metrics-grid">
        {metricCards.map((card) => (
          <MetricCard key={card.title} {...card} />
        ))}
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Club</p>
            <h2>Falhas de criacao de club</h2>
          </div>
          <span className="admin-chip">{clubCreationErrors?.items.length || 0} evento(s)</span>
        </div>

        <div className="admin-kpi-grid">
          <article className="admin-kpi-item"><span>Total 24h</span><strong>{clubCreationErrors?.summary.total_24h || 0}</strong></article>
          <article className="admin-kpi-item"><span>Assinatura bloqueada</span><strong>{clubCreationErrors?.summary.blocked_subscription_24h || 0}</strong></article>
          <article className="admin-kpi-item"><span>Validacao 4xx</span><strong>{clubCreationErrors?.summary.validation_24h || 0}</strong></article>
          <article className="admin-kpi-item"><span>Erro interno 5xx</span><strong>{clubCreationErrors?.summary.internal_24h || 0}</strong></article>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Usuario</th>
                <th>Status</th>
                <th>Motivo</th>
                <th>Payload</th>
              </tr>
            </thead>
            <tbody>
              {clubCreationErrors?.items.length ? (
                clubCreationErrors.items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{formatDateTime(item.created_at)}</strong>
                      <span>{item.request_method || '-'} {item.request_path || '-'}</span>
                    </td>
                    <td>
                      <strong>{item.user_nickname || '-'}</strong>
                      <span>{item.user_email || '-'}</span>
                    </td>
                    <td>
                      <strong>{item.status_code || '-'}</strong>
                      <span>{item.error_code || '-'}</span>
                    </td>
                    <td>{item.message}</td>
                    <td>
                      <strong>{item.payload_name || '-'}</strong>
                      <span>Rake {item.payload_rake_percentage ?? '-'}%</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5}>Nenhuma falha de criacao de club registrada nas ultimas consultas.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Processo</p>
              <h2>Processo e pool do backend</h2>
            </div>
            <span className="admin-chip">Gerado em {formatDateTime(httpMetrics?.generated_at)}</span>
          </div>

          <div className="admin-kpi-grid">
            <article className="admin-kpi-item"><span>Uptime</span><strong>{formatSeconds(summary?.process_uptime_seconds || 0)}</strong></article>
            <article className="admin-kpi-item"><span>RSS</span><strong>{formatMemory(summary?.rss_mb || 0)}</strong></article>
            <article className="admin-kpi-item"><span>Heap usado</span><strong>{formatMemory(summary?.heap_used_mb || 0)}</strong></article>
            <article className="admin-kpi-item"><span>Heap total</span><strong>{formatMemory(summary?.heap_total_mb || 0)}</strong></article>
            <article className="admin-kpi-item"><span>Pool total</span><strong>{summary?.pool_total_connections || 0}</strong></article>
            <article className="admin-kpi-item"><span>Pool idle</span><strong>{summary?.pool_idle_connections || 0}</strong></article>
            <article className="admin-kpi-item"><span>Pool waiting</span><strong>{summary?.pool_waiting_clients || 0}</strong></article>
            <article className="admin-kpi-item"><span>Queries longas</span><strong>{summary?.long_running_queries || 0}</strong></article>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Banco</p>
              <h2>Estado atual do Postgres</h2>
            </div>
          </div>

          <div className="admin-kpi-grid">
            <article className="admin-kpi-item"><span>Conexoes atuais</span><strong>{summary?.db_connections_current || 0}</strong></article>
            <article className="admin-kpi-item"><span>Conexoes ativas</span><strong>{summary?.db_connections_active || 0}</strong></article>
            <article className="admin-kpi-item"><span>Conexoes idle</span><strong>{summary?.db_connections_idle || 0}</strong></article>
            <article className="admin-kpi-item"><span>Requests totais</span><strong>{summary?.requests_total || 0}</strong></article>
            <article className="admin-kpi-item"><span>P50</span><strong>{formatDuration(summary?.p50_duration_ms || 0)}</strong></article>
            <article className="admin-kpi-item"><span>P99</span><strong>{formatDuration(summary?.p99_duration_ms || 0)}</strong></article>
            <article className="admin-kpi-item"><span>Requests lentos</span><strong>{summary?.slow_requests_1s || 0}</strong></article>
            <article className="admin-kpi-item"><span>Loop medio</span><strong>{formatDuration(summary?.event_loop_mean_ms || 0)}</strong></article>
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Rotas</p>
              <h2>Top rotas observadas em memoria</h2>
            </div>
            <span className="admin-chip">{httpMetrics?.routes.length || 0} rota(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Rota</th>
                  <th>Volume</th>
                  <th>Erro</th>
                  <th>Latencia</th>
                  <th>Ultima</th>
                </tr>
              </thead>
              <tbody>
                {httpMetrics?.routes.length ? (
                  httpMetrics.routes.map((route) => (
                    <tr key={`${route.method}-${route.route}`}>
                      <td>
                        <strong>{route.method}</strong>
                        <span>{route.route}</span>
                      </td>
                      <td>
                        <strong>{route.requests_last_5m} em 5m</strong>
                        <span>{route.requests_total} total</span>
                      </td>
                      <td>
                        <strong>{route.error_count}</strong>
                        <span>{route.error_rate_pct}%</span>
                      </td>
                      <td>
                        <strong>{formatDuration(route.avg_duration_ms)}</strong>
                        <span>P95 {formatDuration(route.p95_duration_ms)}</span>
                      </td>
                      <td>{formatDateTime(route.last_seen_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>As metricas HTTP ainda nao receberam trafego suficiente desde que o processo subiu.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Queries</p>
              <h2>Atividade longa em execucao</h2>
            </div>
            <span className="admin-chip">{httpMetrics?.slow_queries.length || 0} query(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>PID</th>
                  <th>Status</th>
                  <th>Duracao</th>
                  <th>App</th>
                  <th>Query</th>
                </tr>
              </thead>
              <tbody>
                {httpMetrics?.slow_queries.length ? (
                  httpMetrics.slow_queries.map((item) => (
                    <tr key={item.pid}>
                      <td>{item.pid}</td>
                      <td>
                        <strong>{item.state}</strong>
                        <span>{item.wait_event_type || '-'}</span>
                      </td>
                      <td>{formatSeconds(item.duration_seconds)}</td>
                      <td>{item.application_name || '-'}</td>
                      <td>{item.query_text || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhuma query longa visivel agora.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Webhooks</p>
              <h2>Falhas recentes</h2>
            </div>
            <span className="admin-chip">{webhooks?.recent_failures.length || 0} falha(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Evento</th>
                  <th>Status</th>
                  <th>Erro</th>
                  <th>Recebido</th>
                </tr>
              </thead>
              <tbody>
                {webhooks?.recent_failures.length ? (
                  webhooks.recent_failures.map((failure) => (
                    <tr key={failure.id}>
                      <td>{failure.club_name || '-'}</td>
                      <td>
                        <strong>{failure.event_type}</strong>
                        <span>{failure.provider}</span>
                      </td>
                      <td>{failure.processing_status}</td>
                      <td>{failure.error_message || '-'}</td>
                      <td>{formatDateTime(failure.received_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhuma falha recente de webhook foi encontrada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Accounts</p>
              <h2>Saude das integracoes</h2>
            </div>
            <span className="admin-chip">{webhooks?.account_health.length || 0} account(s)</span>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Club</th>
                  <th>Status</th>
                  <th>Eventos 24h</th>
                  <th>Ultimo webhook</th>
                  <th>Erro</th>
                </tr>
              </thead>
              <tbody>
                {webhooks?.account_health.length ? (
                  webhooks.account_health.map((account) => (
                    <tr key={account.club_id}>
                      <td>
                        <strong>{account.club_name}</strong>
                        <span>{account.provider} / {account.environment}</span>
                      </td>
                      <td>{account.status}</td>
                      <td>
                        <strong>{account.processed_events_24h} ok</strong>
                        <span>{account.failed_events_24h} fail / {account.pending_events_24h} pending</span>
                      </td>
                      <td>{formatDateTime(account.last_webhook_at)}</td>
                      <td>{account.last_error_message || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>Nenhuma conta de webhook foi consolidada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Jobs</p>
            <h2>Infraestrutura de worker</h2>
          </div>
          <span className="admin-chip">{jobs?.summary.registered_jobs || 0} job(s)</span>
        </div>

        <div className="admin-kpi-grid">
          <article className="admin-kpi-item"><span>Registrados</span><strong>{jobs?.summary.registered_jobs || 0}</strong></article>
          <article className="admin-kpi-item"><span>Rodando</span><strong>{jobs?.summary.running_jobs || 0}</strong></article>
          <article className="admin-kpi-item"><span>Falhos 24h</span><strong>{jobs?.summary.failed_jobs_24h || 0}</strong></article>
          <article className="admin-kpi-item"><span>Em fila</span><strong>{jobs?.summary.queued_jobs || 0}</strong></article>
        </div>

        <div className="admin-note-stack">
          {(jobs?.notes || []).map((note, index) => (
            <div key={`jobs-note-${index}`} className="admin-note-chip">{note}</div>
          ))}
        </div>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Notas</p>
            <h2>Limites desta etapa</h2>
          </div>
        </div>

        <div className="admin-note-stack">
          {(httpMetrics?.notes || []).map((note, index) => (
            <div key={`system-http-note-${index}`} className="admin-note-chip">{note}</div>
          ))}
          {(webhooks?.notes || []).map((note, index) => (
            <div key={`system-webhook-note-${index}`} className="admin-note-chip">{note}</div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default SystemPage;
